/**
 * `AIBehaviour_Executor.ts` — port of Unity's
 * `Executor/AIBehaviour_Executor.cs` for the web-registered AI-behaviour
 * tools. Session-facing behavior goes through the
 * {@link AgentToolExecutorHost} callbacks the orchestrator provides;
 * `present_options` routes through guardrails (interaction partial parity).
 */

import type { IUndoRedo } from "@models/project/IUndoRedo";
import { AgentToolNames } from "@ai/registry/agentToolNames";
import type { IAIGuardrailsModel } from "@ai/guardrails/AIGuardrailsModel";
import type { AIPresentOptionsRound } from "@ai/interface/AIConversationPresentationModel";
import type { AIInterfaceModel } from "@ai/interface/AIInterfaceModel";
import { getRoleIds, tryResolveRoleId, DEFAULT_ROLE_ID } from "@ai/guardrails/agentPrompts";
import {
  getBool,
  getString,
  getStringList,
  type AgentToolExecutorHost,
  type IDomainExecutor,
  type ToolArgs,
} from "./AgentToolExecutorModel";

export class AIBehaviourExecutor implements IDomainExecutor {
  private readonly m_host: AgentToolExecutorHost;
  private readonly m_guardrails: IAIGuardrailsModel;
  private readonly m_undoRedo: IUndoRedo;
  /** Skill/role state owner (Unity executor reaches `m_runtimeOrchestrator.InterfaceModel`). */
  private readonly m_interfaceModel: AIInterfaceModel;

  constructor(
    host: AgentToolExecutorHost,
    guardrails: IAIGuardrailsModel,
    undoRedo: IUndoRedo,
    interfaceModel: AIInterfaceModel,
  ) {
    this.m_host = host;
    this.m_guardrails = guardrails;
    this.m_undoRedo = undoRedo;
    this.m_interfaceModel = interfaceModel;
  }

  async tryExecute(toolName: string, args: ToolArgs): Promise<string | null> {
    switch (toolName) {
      case AgentToolNames.SwitchRole:
        return this.switchRole(args);
      case AgentToolNames.LoadSkill:
        return this.loadSkill(args);
      case AgentToolNames.ChangePersonality: {
        const personality = getString(args, "personality");
        if (!personality) return "error: Change_personality_missing_personality";
        const changed = await this.m_host.setPersonality(personality);
        return changed
          ? `ok: personality changed to '${personality}'`
          : `error: unknown personality '${personality}'`;
      }
      case AgentToolNames.ResetConversation: {
        const reset = await this.m_host.resetConversation();
        return reset ? "ok: conversation reset" : "error: reset_conversation_failed";
      }
      case AgentToolNames.Undo: {
        if (!this.m_undoRedo.canUndo) return "ok: nothing to undo";
        this.m_undoRedo.undo();
        return "ok: undone";
      }
      case AgentToolNames.UseConstantListening: {
        const enabled = getBool(args, "enabled");
        if (enabled === null) return "error: Use_ConstantListening_missing_enabled";
        this.m_host.setConstantListening(enabled);
        return enabled
          ? "ok: constant listening enabled (mic always on)"
          : "ok: push-to-talk enabled";
      }
      case AgentToolNames.ListenUser:
        return this.m_guardrails.tryTriggerListenUser();
      case AgentToolNames.SendUserText: {
        const text = getString(args, "text");
        if (!text) return "error: Send_User_Text_missing_text";
        this.m_host.sendUserText(text);
        return "ok";
      }
      case AgentToolNames.ToggleAIPanel: {
        const enabled = getBool(args, "enabled");
        if (enabled === null) return "error: Toggle_AIPanel_missing_enabled";
        this.m_host.setPanelMinimalMode(enabled);
        return enabled
          ? "ok: panel switched to minimal mode"
          : "ok: full panel always visible";
      }
      case AgentToolNames.ToggleTranscript:
        return this.toggleTranscript(args);
      case AgentToolNames.ToggleDebugMode: {
        const enabled = getBool(args, "enabled");
        if (enabled === null) return "error: Toggle_DebugMode_missing_enabled";
        this.m_host.setDebugMode(enabled);
        return enabled ? "ok: debug mode enabled" : "ok: debug mode disabled";
      }
      case AgentToolNames.ToggleSpark: {
        const visible = getBool(args, "visible");
        if (visible === null) return "error: Toggle_Spark_missing_visible";
        const updated = this.m_host.setSparkVisibility(visible);
        if (!updated) return "error: spark_unavailable";
        return `ok: Spark ${visible ? "visible" : "hidden"}`;
      }
      case AgentToolNames.PresentOptions:
        return this.presentOptions(args);
      default:
        return null;
    }
  }

  /**
   * `Toggle_Transcript` — show or hide the conversation in Spark's panel.
   *
   * The panel owns the three shapes the conversation takes (the floating
   * drawer, the phone's row, and the body of the workflow play panel) and the
   * interface model owns being reachable from here; what this owns is the
   * WORDING, and the distinction each refusal has to draw.
   *
   * The three ways a show does nothing are deliberately separate replies,
   * because the Guide has to say a different thing for each: no panel is
   * mounted at all (a workflow step can withhold the chat, so there is nothing
   * on screen to expand), the conversation is empty (nothing to show — and a
   * guided run drops a script's spoken lines, so "Spark has been talking" does
   * not mean there is a transcript), or minimal-panel mode is suppressing it,
   * which expanding cannot beat.
   */
  private toggleTranscript(args: ToolArgs): string {
    const visible = getBool(args, "visible");
    if (visible === null) return "error: Toggle_Transcript_missing_visible";

    const state = this.m_interfaceModel.setTranscriptExpanded(visible);
    if (state === null) {
      return (
        "error: panel_not_available (Spark's panel is not on screen, so there is no conversation " +
        "to show or hide - a guided workflow step can withhold the chat entirely)"
      );
    }
    if (!visible) return "ok: transcript hidden";
    if (state.isExpanded) return "ok: transcript shown";
    if (!state.hasMessages) {
      return (
        "error: transcript_empty (nothing has been said in this conversation yet, so there is " +
        "nothing to show - tell them rather than trying again)"
      );
    }
    return (
      "error: transcript_suppressed (the panel is in minimal mode, which shows the conversation " +
      `only for citations and option rounds. ${AgentToolNames.ToggleAIPanel} enabled=false brings ` +
      "it back, but that changes their panel setting - offer it rather than doing it unasked)"
    );
  }

  private presentOptions(args: ToolArgs): string {
    const rounds: AIPresentOptionsRound[] = [];

    const optionSets = args.option_sets;
    if (Array.isArray(optionSets)) {
      for (const raw of optionSets) {
        if (typeof raw !== "object" || raw === null) continue;
        const set = raw as Record<string, unknown>;
        const question = typeof set.question === "string" ? set.question : "";
        const options = Array.isArray(set.options)
          ? set.options.filter((o): o is string => typeof o === "string")
          : [];
        rounds.push({ question, options });
      }
    }

    // Legacy single-round prompt + options pair.
    if (rounds.length === 0) {
      const prompt = getString(args, "prompt") ?? "";
      const options = getStringList(args, "options");
      if (options.length > 0) rounds.push({ question: prompt, options });
    }

    const moreInfoRequired = getBool(args, "more_info_required") ?? false;
    return this.m_guardrails.tryPresentOptions(rounds, moreInfoRequired);
  }

  /** Port of Unity `AIBehaviour_Executor.SwitchRole` (result strings verbatim). */
  private switchRole(args: ToolArgs): string {
    const roleStr = getString(args, "role");
    if (!roleStr?.trim()) {
      return `error: role is required. Valid roles: ${getRoleIds().join(", ")}.`;
    }

    const roleId = tryResolveRoleId(roleStr);
    if (!roleId) {
      return `error: invalid role '${roleStr}'. Valid roles: ${getRoleIds().join(", ")}.`;
    }

    if (this.m_host.getCurrentRole().toLowerCase() !== roleId.toLowerCase()) {
      this.m_host.setAgentRole(roleId);
    }

    return `ok: role set to ${roleId}`;
  }

  /** Port of Unity `AIBehaviour_Executor.LoadSkill` (result strings verbatim). */
  private loadSkill(args: ToolArgs): string {
    const skillId = getString(args, "skill_id");
    if (!skillId?.trim()) {
      return "error: skill_id is required.";
    }

    const currentRoleId = this.m_host.getCurrentRole() || DEFAULT_ROLE_ID;
    const target = skillId.trim().toLowerCase();
    const skillDefinition =
      this.m_interfaceModel.availableSkills.find((s) => s.id.toLowerCase() === target) ?? null;

    if (!skillDefinition) {
      const availableIds = this.m_interfaceModel.availableSkillsForActiveRole
        .filter((s) => s.id.trim())
        .map((s) => s.id)
        .join(", ");
      return availableIds
        ? `error: unknown skill_id '${skillId}'. Skills available in the ${currentRoleId} role: ${availableIds}.`
        : `error: unknown skill_id '${skillId}'. No skills are available in the ${currentRoleId} role.`;
    }

    if (!this.m_interfaceModel.isSkillInRoleScope(skillDefinition, currentRoleId)) {
      // The EFFECTIVE roles, not the file's `roles:` list. The account can add
      // or withdraw a role for a skill, so the two differ whenever an override
      // is in play — and this string is the agent's only instruction about
      // where to go, so naming a role that can no longer reach the skill sends
      // it into a switch_role that will not help.
      const allowedRoles = this.m_interfaceModel.getEffectiveSkillRoles(skillDefinition);
      return allowedRoles.length > 0
        ? `error: skill '${skillId}' is not available in the ${currentRoleId} role. Switch to one of these roles first with switch_role: ${allowedRoles.join(", ")}.`
        : `error: skill '${skillId}' is not available in any role.`;
    }

    const loadedSkill = this.m_interfaceModel.tryLoadSkill(skillDefinition.id);
    if (!loadedSkill) {
      return `error: failed to load skill '${skillId}'.`;
    }

    const body = loadedSkill.markdown.trim() || "(no additional guidance provided)";
    return `ok: loaded skill '${loadedSkill.displayName}'. Apply this guidance for the rest of the session:\n\n${body}`;
  }
}
